import { useEffect, useState } from 'react';
import {
  searchWorkflows,
  resumeWorkflow,
  pauseWorkflow,
  cancelWorkflow,
  archiveWorkflow,
} from 'services/workflowService';
import {
  ISearchWorkFlowRequest,
  IWorkflowActionRequest,
  IWorkflowRun,
} from 'interface/workflowInterface';
import CommonTable from 'components/common/TableCommon';
import { createHeadCellList } from './common';
import { useNavigate } from 'react-router-dom';
import { Path } from 'router/constant';
import { RootState } from 'redux-setup/store';
import { useAppSelector } from 'hooks/reduxHooks';
import { toast } from 'react-toastify';

export const Workflows = () => {
  const navigate = useNavigate();
  const [workflowsData, setWorkflowsData] = useState<IWorkflowRun[]>([]);
  const [isNextAPIDisabled, setIsNextAPIDisabled] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const limit = 25;

  const organizationId = useAppSelector(
    (state: RootState) => state.organization.id,
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(
    null,
  );

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    workflowId: string,
  ) => {
    event.stopPropagation();
    setSelectedWorkflowId(workflowId);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedWorkflowId(null);
  };

  const fetchWorkflows = async () => {
    try {
      const payload: ISearchWorkFlowRequest = {
        Page: page - 1,
        Limit: limit,
        OrganizationID: organizationId,
        Descending: false,
        OrderBy: 'Name',
      };
      const response = await searchWorkflows(payload);
      const err = response.check();
      if (err) {
        toast.error('Failed to fetch workflows');
        return;
      }
      const workflows = response.unwrap();
      if (workflows.Result && workflows.Result.length > 0) {
        setWorkflowsData(prev => [...prev, ...workflows.Result]);
        setIsNextAPIDisabled(workflows.Result.length < limit);
      } else {
        setWorkflowsData([]);
      }
    } catch (error) {
      console.error('Error fetching workflows:', error);
      toast.error('Failed to fetch workflows');
    }
  };

  useEffect(() => {
    fetchWorkflows();
  }, [page]);

  const handleRowClick = async (workflow: IWorkflowRun) => {
    navigate(`${Path.WORKFLOWS}/${workflow.ID}`);
  };

  const onResumeWorkflow = async (workflowId: string) => {
    try {
      const payload: IWorkflowActionRequest = {
        WorkflowRunID: workflowId,
      };
      const response = await resumeWorkflow(payload);
      const err = response.check();
      if (err) {
        toast.error('Failed to resume workflow');
        return;
      }
      toast.success('Workflow resumed successfully');
      fetchWorkflows();
    } catch (error) {
      console.error('Error resuming workflow:', error);
      toast.error('Failed to resume workflow');
    }
  };

  const onPauseWorkflow = async (workflowId: string) => {
    try {
      const payload: IWorkflowActionRequest = {
        WorkflowRunID: workflowId,
      };
      const response = await pauseWorkflow(payload);
      const err = response.check();
      if (err) {
        toast.error('Failed to pause workflow');
        return;
      }
      toast.success('Workflow paused successfully');
      fetchWorkflows();
    } catch (error) {
      console.error('Error pausing workflow:', error);
      toast.error('Failed to pause workflow');
    }
  };

  const onCancelWorkflow = async (workflowId: string) => {
    try {
      const payload: IWorkflowActionRequest = {
        WorkflowRunID: workflowId,
      };
      const response = await cancelWorkflow(payload);
      const err = response.check();
      if (err) {
        toast.error('Failed to cancel workflow');
        return;
      }
      toast.success('Workflow canceled successfully');
      fetchWorkflows();
    } catch (error) {
      console.error('Error canceling workflow:', error);
      toast.error('Failed to cancel workflow');
    }
  };

  const onArchiveWorkflow = async (workflowId: string) => {
    try {
      const payload: IWorkflowActionRequest = {
        WorkflowRunID: workflowId,
      };
      const response = await archiveWorkflow(payload);
      const err = response.check();
      if (err) {
        toast.error('Failed to archive workflow');
        return;
      }
      toast.success('Workflow archived successfully');
      fetchWorkflows();
    } catch (error) {
      console.error('Error archiving workflow:', error);
      toast.error('Failed to archive workflow');
    }
  };

  return (
    <>
      <CommonTable<IWorkflowRun>
        headCellList={createHeadCellList(
          onResumeWorkflow,
          onPauseWorkflow,
          onCancelWorkflow,
          onArchiveWorkflow,
          handleMenuOpen,
          handleMenuClose,
          anchorEl,
          selectedWorkflowId,
        )}
        data={workflowsData}
        isNextAPIDisabled={isNextAPIDisabled}
        onEndReached={() => {
          setPage(prev => prev + 1);
        }}
        onRowClick={handleRowClick}
      />
    </>
  );
};
